import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SPRINT_PALETTE } from '../color';

/**
 * Chọn màu cho sprint — một hàng ô màu, thêm ô "tự chọn".
 *
 * ## Vì sao bảng màu có sẵn, không phải `<input type="color">`
 *
 * Ô chọn màu của trình duyệt cho ra 16 triệu màu, và phần lớn trong số đó là
 * màu không đọc nổi trên nền tối hoặc lẫn với đỏ/vàng/xanh của RAG. Tám ô đủ
 * để phân biệt các sprint đang chạy trong một dự án — vốn là việc duy nhất màu
 * này phải làm.
 *
 * ## Vì sao vẫn có "tự chọn"
 *
 * `null` không phải là "không màu": giao diện vẫn suy một màu ổn định từ `id`.
 * Nó là "để hệ thống chọn" — và nó phải bấm lại được, nếu không thì người lỡ
 * tay chọn một màu sẽ không có đường quay về.
 */
export function SprintColorPicker({
  value,
  onChange,
  className,
}: {
  /** `null` = để hệ thống tự chọn theo `id`. */
  readonly value: string | null;
  readonly onChange: (color: string | null) => void;
  readonly className?: string;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <div
      role="radiogroup"
      aria-label={t('sprintEdit.color')}
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      {SPRINT_PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={color}
          onClick={() => onChange(color)}
          style={{ backgroundColor: color }}
          className={cn(
            'flex size-6 items-center justify-center rounded-full ring-offset-1 ring-offset-background transition-shadow',
            value === color && 'ring-2 ring-foreground',
          )}
        >
          {/* Dấu tích chứ không chỉ có viền: viền quanh một ô màu là kênh thứ
              hai của **cùng** một thuộc tính — người mù màu thấy hai ô sẫm
              giống nhau, một cái đang chọn và một cái không. */}
          {value === color ? <Check aria-hidden className="size-3.5 text-white" /> : null}
        </button>
      ))}

      <button
        type="button"
        role="radio"
        aria-checked={value === null}
        onClick={() => onChange(null)}
        className={cn(
          'h-6 rounded-full border border-dashed border-border px-2 text-[11px] text-muted-foreground',
          value === null && 'border-solid border-foreground text-foreground',
        )}
      >
        {t('sprintEdit.colorAuto')}
      </button>
    </div>
  );
}
